/* eslint-disable jsx-a11y/label-has-associated-control */
import styled from '@emotion/styled';
import Grid from '@react-css/grid';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Button from 'components/Common/Button';
import { FormFieldLabel } from 'components/Common/FormField';
import ShopifyFormField from 'components/Common/FormField/ShopifyFormField';
import Spinner from 'components/Common/Spinner';
import Icon from 'storybookConfig/stories/molecules/Icon';
import { selectIsSuperUser } from 'store/selectors/me/user';

const FormGroupButton = styled(Button)`
  margin-bottom: 1.375rem;
  width: 95%;
`;

const AccessTokenField = ({ error, register }) => {
  const cn = classNames('form-group');
  const accessTokenInputClassNames = classNames(
    'form-control',
    'form-control-prepended',
    'form-control-appended',
    {
      [`form-control-sm`]: 'sm',
    }
  );
  const [showAccessToken, setShowAccessToken] = useState(false);

  return (
    <div className={cn}>
      <FormFieldLabel size="sm" htmlFor="input-field">
        Shopify Access Token
      </FormFieldLabel>
      <div className="input-group input-group-sm">
        <input
          className={accessTokenInputClassNames}
          type={showAccessToken ? 'text' : 'password'}
          id="input-field"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register('updatedToken', {
            defaultValue: '',
            required: true,
          })}
        />
        <div className="input-group-append">
          <div className="input-group-text">
            <Icon
              name={showAccessToken ? 'visibility_off' : 'visibility'}
              onClick={() => setShowAccessToken(!showAccessToken)}
            />
          </div>
        </div>
      </div>
      {error ? <div className="invalid-feedback d-block">{error.message}</div> : null}
    </div>
  );
};

const ProductCollectionIdField = ({ error, register, defaultValue }) => {
  const cn = classNames('form-group');
  const inputClassNames = classNames(
    'form-control',
    'form-control-prepended',
    'form-control-appended',
    {
      [`form-control-sm`]: 'sm',
    }
  );

  return (
    <div className={cn}>
      <FormFieldLabel size="sm" htmlFor="product-collection-id">
        Product Collection ID
      </FormFieldLabel>
      <div className="input-group input-group-sm">
        <input
          className={inputClassNames}
          type="text"
          id="product-collection-id"
          placeholder="Enter Shopify collection ID"
          defaultValue={defaultValue}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register('productCollectionId', {
            defaultValue: defaultValue || '',
          })}
        />
      </div>
      {error ? <div className="invalid-feedback d-block">{error.message}</div> : null}
    </div>
  );
};

const EditShopifySettings = ({
  isEditable,
  isEditingAccessToken,
  onSubmit,
  onCancel,
  store,
  productCollectionId = '',
}) => {
  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });

  const pendingUpdate = useSelector(({ company: c }) => c.isUpdatingPlatformAuth);
  const isSuperUser = useSelector(selectIsSuperUser);

  const watchedValues = watch();

  useEffect(() => reset(), [reset]);

  const submitForm = (values) => handleSubmit(onSubmit)(values).then(() => reset());

  // Determine if form is valid based on what's being edited
  const isFormValid = () => {
    if (isEditingAccessToken) {
      return watchedValues.updatedToken && watchedValues.updatedToken.trim() !== '';
    }
    if (isEditable && watchedValues.updatedStore) {
      return watchedValues.updatedStore && watchedValues.updatedStore.trim() !== '';
    }
    if (isSuperUser && watchedValues.productCollectionId !== undefined) {
      // Check if the value has actually changed from the original
      const hasChanged = watchedValues.productCollectionId !== productCollectionId;
      return hasChanged; // Allow submission if value has changed
    }
    return false;
  };

  return (
    <form onSubmit={submitForm}>
      <Grid>
        {isEditable && (
          <ShopifyFormField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('updatedStore', {
              required: isEditable ? 'Please enter a store name' : false,
              pattern: {
                value: /^[-a-zA-Z0-9]+$/,
                message: 'Please enter a valid store name',
              },
            })}
            error={errors.store}
            size="sm"
            defaultValue={store}
          />
        )}
        {isEditingAccessToken && (
          <AccessTokenField error={errors.updatedToken} register={register} />
        )}
        {isSuperUser && (
          <ProductCollectionIdField
            error={errors.productCollectionId}
            register={register}
            defaultValue={productCollectionId || ''}
          />
        )}
      </Grid>
      <Grid columns="1fr 1fr 1fr 1fr" alignContentEnd justifyItemsEnd>
        <div />
        <div />
        <FormGroupButton size="sm" type="button" color="secondary" outline onClick={onCancel}>
          Cancel
        </FormGroupButton>
        <FormGroupButton
          size="sm"
          type="submit"
          color={isFormValid() ? 'primary' : 'dark'}
          disabled={!isFormValid()}
        >
          {pendingUpdate ? <Spinner color="primary" small className="mx-3" /> : 'Save'}
        </FormGroupButton>
      </Grid>
    </form>
  );
};

export default EditShopifySettings;
